import { type JSX, Match, Show, Switch } from 'solid-js';
import { t, tPlural } from '../../i18n';
import { isForbidden } from '../../lib/api';
import { useNotificationEstimate } from '../../lib/notification-estimate';
import { Skeleton } from '../Skeleton';
import { Button } from '../ui/Button';

// The weekly estimate (notifications overhaul, slice 21): "About 9 emails a
// week with these settings", under the page header. The number is a function of
// the settings BELOW it, so every settings write re-reads it
// (`refreshNotificationEstimate`, wired in `NotificationTiers`); while that
// re-read is in flight the OLD number stays
// on screen and only `aria-busy` moves — the reader is mid-decision, and
// blanking the number they are comparing presets against is the one thing this
// line must never do.
//
// The whole box is one polite live region: the number is announced when it
// changes, and nothing else here speaks.
//
// Failure is QUIET by design (the slice's ruling): the estimate is a
// convenience, not the page's subject. A failed read paints one muted line and
// a small retry, never an `<ErrorState/>` box — the settings below are still
// fully usable, and a loud error above them would claim otherwise. A 403 paints
// nothing at all: the server answered, "no estimate for you" needs no words,
// and a Retry on a denial would be a button that always fails.

export function NotificationEstimate(props: { token: string | null }): JSX.Element {
  const query = useNotificationEstimate(props.token);

  /** The headline: floor or exact by the data, its plural by the count. */
  const line = (data: { emails: number; truncated: boolean }): string =>
    tPlural(
      data.truncated
        ? 'notifications_settings.estimate.line_floor'
        : 'notifications_settings.estimate.line',
      data.emails,
    );

  return (
    <div class="notification-estimate" aria-live="polite" aria-busy={query.isFetching}>
      <Switch>
        {/* A 403 is an answer, not a failure: paint nothing, offer nothing. */}
        <Match when={query.isError && isForbidden(query.error)}>{undefined}</Match>
        <Match when={query.isError}>
          <p class="notification-estimate__error">{t('notifications_settings.estimate.error')}</p>
          <Button variant="ghost" size="sm" onClick={() => void query.refetch()}>
            {t('notifications_settings.estimate.retry')}
          </Button>
        </Match>
        <Match when={query.data}>
          {(data) => (
            <>
              <p class="notification-estimate__line">
                {/* `truncated` makes every total a floor, so the wording says
                    so: "At least {n}" — never a number the server did not
                    bound (`notification-estimate.ts` module header). */}
                {data().events === 0
                  ? t('notifications_settings.estimate.no_history')
                  : line({ emails: data().current.emails, truncated: data().truncated })}
              </p>
              <Show when={data().events > 0}>
                <p class="notification-estimate__basis">
                  {tPlural('notifications_settings.estimate.basis', data().events, {
                    days: data().window_days,
                    events: data().events,
                  })}
                </p>
              </Show>
            </>
          )}
        </Match>
        <Match when={query.isLoading}>
          <Skeleton height="1.25rem" width="18rem" />
        </Match>
      </Switch>
    </div>
  );
}
